import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import EditNoteIcon from "@mui/icons-material/EditNote";
import BookmarksOutlinedIcon from "@mui/icons-material/BookmarksOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import moment from "moment";
import { toast } from "react-toastify";
import secureLocalStorage from "react-secure-storage";

import { useDefaultStyles } from "../../../common/styles/default";
import palette from "../../../common/styles/palette";
import fonts from "../../../common/styles/fonts";
import LoaderHiplade from "../../../common/components/loaderhiplade";
import { useModal } from "../../../common/hooks/useModal";
import CheckListService from "../../../services/CheckListService";
import MarkersService from "../../../services/MarkersService";
import { IMarker } from "../../../types/Markers";
import StudentFeedbackService, {
  StudentFeedback,
  StudentFeedbackPayload,
} from "../../../services/StudentFeedbackService";
import useChecklistAiResult from "../../hooks/useChecklistAiResult";
import {
  AiBlocoTitulo,
  AiCallout,
  AiCard,
  AiPill,
  AiSectionHeader,
} from "../modules/aiSurface";
import { sementeDaAnalise } from "./studentFeedbackSeed";

const formatMarkerTime = (time: string | number | undefined) => {
  if (time === undefined || time === null || time === "") return "00:00:00";
  if (typeof time === "number") {
    return moment.utc(time * 1000).format("HH:mm:ss");
  }
  if (!isNaN(Number(time))) {
    return moment.utc(Number(time) * 1000).format("HH:mm:ss");
  }
  return String(time);
};

/**
 * Aba "Feedback" do Debriefing — o Feedback ao estudante do conceito
 * (hiplade.lovable.app/debriefing): visão geral, principais acertos, pontos a
 * melhorar, recomendações e orientação final, com rascunho do avaliador e
 * publicação para o aluno.
 *
 * Quando ainda não existe rascunho, os campos nascem pré-preenchidos da
 * análise de vídeo da IA (`studentFeedbackSeed.ts`) — o avaliador revisa
 * antes de publicar. O aluno (role 5) entra aqui em modo leitura e só vê o
 * feedback depois de publicado: o backend responde 404 para rascunho de
 * quem não pode escrever debriefing.
 */

interface ListaDePontosProps {
  itens: string[];
  cor: string;
  adicionarRotulo: string;
  readOnly: boolean;
  onChange: (itens: string[]) => void;
}

/** A lista de bullets editável (acertos / melhorias / recomendações). */
function ListaDePontos({
  itens,
  cor,
  adicionarRotulo,
  readOnly,
  onChange,
}: ListaDePontosProps) {
  if (readOnly) {
    return (
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {itens.map((texto, indice) => (
          <Box
            component="li"
            key={indice}
            sx={{ color: palette.text, fontSize: "0.9375rem", mb: 1, "&::marker": { color: cor } }}
          >
            {texto}
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box>
      {itens.map((texto, indice) => (
        <Box
          key={indice}
          sx={{ display: "flex", gap: 1, mb: 1, alignItems: "flex-start" }}
        >
          <Box
            component="span"
            sx={{
              mt: 1.8,
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor: cor,
              flexShrink: 0,
            }}
          />
          <TextField
            fullWidth
            multiline
            size="small"
            value={texto}
            onChange={(evento) =>
              onChange(
                itens.map((item, i) => (i === indice ? evento.target.value : item))
              )
            }
            sx={{ "& .MuiOutlinedInput-root": { fontSize: "0.875rem" } }}
          />
          <IconButton
            size="small"
            aria-label="remover ponto"
            onClick={() => onChange(itens.filter((_, i) => i !== indice))}
            sx={{ color: palette.textMuted, "&:hover": { color: palette.dangerText } }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={() => onChange([...itens, ""])}
        sx={{
          color: palette.brand,
          textTransform: "none",
          fontWeight: 600,
          px: 0.5,
        }}
      >
        {adicionarRotulo}
      </Button>
    </Box>
  );
}

/** Título de seção com ícone — a linha que abre cada card do conceito. */
function TituloSecao({
  icone,
  children,
}: {
  icone?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
      {icone}
      <AiBlocoTitulo sx={{ mb: 0 }}>{children}</AiBlocoTitulo>
    </Box>
  );
}

export default function EventAppraiserFeedback({ debriefingId, station }: any) {
  const styles = useDefaultStyles();
  const isAluno = secureLocalStorage.getItem("role") == 5;

  const { status: statusAnalise, analise } = useChecklistAiResult(debriefingId);

  const [carregando, setCarregando] = useState(true);
  const [feedback, setFeedback] = useState<StudentFeedback | null>(null);
  const [overview, setOverview] = useState("");
  const [strengths, setStrengths] = useState<string[]>([]);
  const [improvements, setImprovements] = useState<string[]>([]);
  const [recommendations, setRecommendations] = useState<string[]>([]);
  const [finalGuidance, setFinalGuidance] = useState("");
  const [perguntaPorItem, setPerguntaPorItem] = useState<Record<string, string>>({});
  const [semeado, setSemeado] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [publicando, setPublicando] = useState(false);
  const [markers, setMarkers] = useState<IMarker[]>([]);
  const [carregandoMarcadores, setCarregandoMarcadores] = useState(false);

  const { handleOpenModal } = useModal();

  useEffect(() => {
    let vivo = true;
    if (!debriefingId) return;

    setCarregandoMarcadores(true);
    MarkersService.getMarkerByDebriefing(debriefingId)
      .then((response: any) => {
        if (!vivo) return;
        const data = response?.data;
        if (Array.isArray(data)) {
          setMarkers(data);
        } else {
          setMarkers([]);
        }
      })
      .catch(() => {
        if (vivo) setMarkers([]);
      })
      .finally(() => {
        if (vivo) setCarregandoMarcadores(false);
      });

    return () => {
      vivo = false;
    };
  }, [debriefingId]);

  const handleOpenMarkerModal = (marker: IMarker) => {
    handleOpenModal({
      displayName: "editScreenshot",
      data: marker,
    });
  };

  useEffect(() => {
    let vivo = true;
    setCarregando(true);

    StudentFeedbackService.getStudentFeedback(debriefingId)
      .then((response) => {
        if (!vivo) return;
        const salvo = response.data;
        setFeedback(salvo);
        setOverview(salvo.overview ?? "");
        setStrengths(salvo.strengths ?? []);
        setImprovements(salvo.improvements ?? []);
        setRecommendations(salvo.recommendations ?? []);
        setFinalGuidance(salvo.final_guidance ?? "");
      })
      .catch(() => {
        if (vivo) setFeedback(null);
      })
      .finally(() => {
        if (vivo) setCarregando(false);
      });

    // O enunciado dos Itens não viaja no JSON da análise — a semente precisa
    // do mesmo mapa que a aba Dados IA monta para não escrever "Item 519".
    if (station?.checklist_id) {
      CheckListService.getChecklistDebriefing(station.checklist_id, debriefingId)
        .then((resposta: any) => {
          if (!vivo) return;
          const mapa: Record<string, string> = {};
          (resposta?.data?.skills || []).forEach((skill: any) =>
            (skill.items || []).forEach((item: any) => {
              if (item.question) mapa[String(item.item_id)] = item.question;
            })
          );
          setPerguntaPorItem(mapa);
        })
        .catch(() => {});
    }

    return () => {
      vivo = false;
    };
  }, [debriefingId, station?.checklist_id]);

  // Semente da IA: só quando NÃO há feedback salvo — um rascunho existente
  // é trabalho do avaliador e nunca é sobrescrito pela análise.
  useEffect(() => {
    if (isAluno || carregando || semeado || feedback) return;
    const semente = sementeDaAnalise(analise, perguntaPorItem);
    if (!semente) return;
    setOverview(semente.overview);
    setStrengths(semente.strengths);
    setImprovements(semente.improvements);
    setSemeado(true);
  }, [analise, perguntaPorItem, carregando, semeado, feedback, isAluno]);

  const payload = (): StudentFeedbackPayload => ({
    overview: overview.trim(),
    strengths: strengths.map((item) => item.trim()).filter(Boolean),
    improvements: improvements.map((item) => item.trim()).filter(Boolean),
    recommendations: recommendations.map((item) => item.trim()).filter(Boolean),
    final_guidance: finalGuidance.trim(),
  });

  const salvar = async (): Promise<StudentFeedback | null> => {
    setSalvando(true);
    try {
      const response = await StudentFeedbackService.saveStudentFeedback(
        debriefingId,
        payload()
      );
      setFeedback(response.data);
      return response.data;
    } catch (error: any) {
      toast(error?.response?.data?.Msg ?? "Não foi possível salvar o feedback.", {
        type: "error",
        position: "top-center",
      });
      return null;
    } finally {
      setSalvando(false);
    }
  };

  const salvarRascunho = async () => {
    if (await salvar()) {
      toast("Rascunho salvo.", { type: "success", position: "top-center" });
    }
  };

  const publicar = async () => {
    setPublicando(true);
    try {
      if (!(await salvar())) return;
      const response = await StudentFeedbackService.publishStudentFeedback(debriefingId);
      setFeedback(response.data);
      toast("Feedback publicado para o estudante.", {
        type: "success",
        position: "top-center",
      });
    } catch (error: any) {
      toast(error?.response?.data?.Msg ?? "Não foi possível publicar o feedback.", {
        type: "error",
        position: "top-center",
      });
    } finally {
      setPublicando(false);
    }
  };

  const publicadoEm = feedback?.published_at
    ? new Date(feedback.published_at).toLocaleDateString("pt-BR")
    : null;

  const pillEstado =
    feedback?.status === "published" ? (
      <AiPill tom="sucesso" label={`Publicado${publicadoEm ? ` em ${publicadoEm}` : ""}`} />
    ) : feedback ? (
      <AiPill tom="atencao" label="Rascunho — não visível para o aluno" />
    ) : semeado ? (
      <AiPill tom="marca" label="Gerado a partir da análise da IA" />
    ) : null;

  const campoOrientacao = isAluno ? (
    <Typography sx={{ color: "#fff", fontSize: "0.9375rem", whiteSpace: "pre-wrap" }}>
      {finalGuidance}
    </Typography>
  ) : (
    <TextField
      fullWidth
      multiline
      minRows={3}
      value={finalGuidance}
      onChange={(evento) => setFinalGuidance(evento.target.value)}
      placeholder="Escreva uma orientação final para o estudante..."
      sx={{
        "& .MuiOutlinedInput-root": {
          color: "#fff",
          fontSize: "0.9375rem",
          backgroundColor: "rgba(255,255,255,0.08)",
          "& fieldset": { borderColor: "rgba(255,255,255,0.25)" },
          "&:hover fieldset": { borderColor: "rgba(255,255,255,0.4)" },
          "&.Mui-focused fieldset": { borderColor: "rgba(255,255,255,0.6)" },
        },
        "& .MuiInputBase-input::placeholder": {
          color: "rgba(255,255,255,0.6)",
          opacity: 1,
        },
      }}
    />
  );

  if (isAluno && !carregando && !feedback && markers.length === 0) {
    return (
      <Box className={styles.container}>
        <AiCallout tom="atencao" titulo="Nenhum feedback publicado ainda.">
          Quando o avaliador publicar o retorno desta estação, ele aparece aqui.
        </AiCallout>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      {carregando && <LoaderHiplade />}

      <AiSectionHeader
        icone={<EditNoteIcon sx={{ color: palette.brand, fontSize: 28 }} />}
        titulo="Feedback ao estudante"
        subtitulo={
          isAluno
            ? "O retorno do avaliador sobre o seu desempenho nesta estação."
            : "Revise, edite e publique o retorno ao aluno. O rascunho inicial é montado a partir da análise de vídeo da IA, quando disponível."
        }
        acao={
          !isAluno && (
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
              {pillEstado}
              <Button
                variant="outlined"
                onClick={salvarRascunho}
                disabled={salvando || publicando}
                sx={{
                  color: palette.brand,
                  borderColor: palette.border,
                  textTransform: "uppercase",
                  fontWeight: 700,
                  "&:hover": { borderColor: palette.brand },
                }}
              >
                Salvar rascunho
              </Button>
              <Button
                variant="contained"
                onClick={publicar}
                disabled={salvando || publicando}
                sx={{
                  backgroundColor: palette.brand,
                  textTransform: "uppercase",
                  fontWeight: 700,
                  "&:hover": { backgroundColor: palette.brandDark },
                }}
              >
                Publicar para o estudante
              </Button>
            </Box>
          )
        }
      />

      {isAluno && !feedback && markers.length > 0 && (
        <AiCallout tom="atencao" titulo="Parecer textual pendente de publicação.">
          O avaliador ainda está redigindo o parecer textual desta estação. Abaixo você já pode conferir as marcações registradas no vídeo da sua simulação.
        </AiCallout>
      )}

      {!isAluno && !feedback && statusAnalise === "in_progress" && (
        <AiCallout tom="neutro" titulo="A análise da IA está em processamento.">
          Ao concluir, os campos abaixo são pré-preenchidos com os vereditos dela
          para você revisar.
        </AiCallout>
      )}

      {(overview || !isAluno) && (
        <AiCard>
          <TituloSecao>Visão geral</TituloSecao>
          {isAluno ? (
            <Typography sx={{ color: palette.text, fontSize: "0.9375rem", whiteSpace: "pre-wrap" }}>
              {overview}
            </Typography>
          ) : (
            <TextField
              fullWidth
              multiline
              minRows={3}
              value={overview}
              onChange={(evento) => setOverview(evento.target.value)}
              placeholder="Resumo do desempenho do estudante na estação..."
              sx={{ "& .MuiOutlinedInput-root": { fontSize: "0.9375rem" } }}
            />
          )}
        </AiCard>
      )}

      {(() => {
        // Em leitura (aluno), seção vazia some: card em branco é ruído.
        const mostraAcertos = !isAluno || strengths.length > 0;
        const mostraMelhorar = !isAluno || improvements.length > 0;
        const largura = mostraAcertos && mostraMelhorar ? 6 : 12;
        return (
          <Grid container spacing={2.5}>
            {mostraAcertos && (
              <Grid item xs={12} md={largura}>
                <AiCard sx={{ height: "100%", boxSizing: "border-box" }}>
                  <TituloSecao
                    icone={
                      <CheckCircleOutlineIcon sx={{ fontSize: 18, color: palette.emeraldDeep }} />
                    }
                  >
                    Principais acertos
                  </TituloSecao>
                  <ListaDePontos
                    itens={strengths}
                    cor={palette.emeraldStrong}
                    adicionarRotulo="Adicionar acerto"
                    readOnly={isAluno}
                    onChange={setStrengths}
                  />
                </AiCard>
              </Grid>
            )}
            {mostraMelhorar && (
              <Grid item xs={12} md={largura}>
                <AiCard sx={{ height: "100%", boxSizing: "border-box" }}>
                  <TituloSecao
                    icone={<WarningAmberIcon sx={{ fontSize: 18, color: palette.warning }} />}
                  >
                    Pontos a melhorar
                  </TituloSecao>
                  <ListaDePontos
                    itens={improvements}
                    cor={palette.warning}
                    adicionarRotulo="Adicionar ponto"
                    readOnly={isAluno}
                    onChange={setImprovements}
                  />
                </AiCard>
              </Grid>
            )}
          </Grid>
        );
      })()}

      {(recommendations.length > 0 || !isAluno) && (
        <AiCard>
          <TituloSecao>Recomendações para o próximo treino</TituloSecao>
          <ListaDePontos
            itens={recommendations}
            cor={palette.brand}
            adicionarRotulo="Adicionar recomendação"
            readOnly={isAluno}
            onChange={setRecommendations}
          />
        </AiCard>
      )}

      {(finalGuidance || !isAluno) && (
        <Box
          sx={{
            borderRadius: `${palette.radius}px`,
            background: `linear-gradient(135deg, ${palette.brandDark}, ${palette.brandDeep})`,
            p: 2.5,
            mb: 2.5,
            boxShadow: palette.shadowSm,
          }}
        >
          <AiBlocoTitulo sx={{ color: "rgba(255,255,255,0.75)" }}>
            Orientação final
          </AiBlocoTitulo>
          {campoOrientacao}
        </Box>
      )}

      {(markers.length > 0 || !isAluno) && (
        <AiCard sx={{ mb: 2.5 }}>
          <TituloSecao
            icone={
              <BookmarksOutlinedIcon sx={{ fontSize: 20, color: palette.brand }} />
            }
          >
            Marcadores da avaliação
          </TituloSecao>

          <Typography sx={{ color: palette.textMuted, fontSize: "0.875rem", mb: 2 }}>
            {isAluno
              ? "Marcações e anotações feitas no vídeo durante a avaliação. Clique em um marcador para visualizar o momento com as anotações e desenhos do professor."
              : "Marcações registradas no vídeo durante a simulação. Clique em um marcador para visualizar ou editar os apontamentos."}
          </Typography>

          {carregandoMarcadores ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={28} sx={{ color: palette.brand }} />
            </Box>
          ) : markers.length === 0 ? (
            <Typography
              sx={{
                color: palette.textMuted,
                fontSize: "0.875rem",
                fontStyle: "italic",
                py: 1,
              }}
            >
              Nenhum marcador registrado no vídeo desta simulação.
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {markers.map((marker, index) => {
                const formattedTime = formatMarkerTime(marker?.time);
                const thumbnail = marker?.attachments?.[0]?.static_url;

                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={marker.id || index}>
                    <Box
                      onClick={() => handleOpenMarkerModal(marker)}
                      sx={{
                        cursor: "pointer",
                        borderRadius: `${palette.radius}px`,
                        border: `1px solid ${palette.border}`,
                        backgroundColor: palette.surface,
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          borderColor: palette.brand,
                          boxShadow: palette.shadowSm,
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      {/* Área da miniatura do frame com timestamp */}
                      <Box
                        sx={{
                          position: "relative",
                          width: "100%",
                          height: 130,
                          backgroundColor: palette.muted,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                        }}
                      >
                        {thumbnail ? (
                          <Box
                            component="img"
                            src={thumbnail}
                            alt={`Marcador ${index + 1}`}
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              color: palette.textMuted,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <VideocamOutlinedIcon sx={{ fontSize: 32 }} />
                            <Typography sx={{ fontSize: "0.75rem" }}>
                              Sem captura
                            </Typography>
                          </Box>
                        )}

                        <Box
                          sx={{
                            position: "absolute",
                            bottom: 8,
                            left: 8,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            backgroundColor: "rgba(0, 0, 0, 0.72)",
                            color: "#fff",
                            px: 1,
                            py: 0.25,
                            borderRadius: "4px",
                            fontFamily: fonts.mono,
                            fontSize: "0.75rem",
                            fontWeight: 600,
                          }}
                        >
                          <AccessTimeOutlinedIcon sx={{ fontSize: 13 }} />
                          <span>{formattedTime}</span>
                        </Box>
                      </Box>

                      {/* Informações e anotação do marcador */}
                      <Box
                        sx={{
                          p: 1.5,
                          flexGrow: 1,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.8125rem",
                            fontWeight: 700,
                            color: palette.brand,
                            mb: 0.5,
                          }}
                        >
                          Marcador #{index + 1}
                        </Typography>

                        <Typography
                          sx={{
                            fontSize: "0.875rem",
                            color: marker.anotation
                              ? palette.text
                              : palette.textMuted,
                            fontStyle: marker.anotation ? "normal" : "italic",
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            lineHeight: 1.4,
                          }}
                        >
                          {marker.anotation
                            ? marker.anotation
                            : "Sem anotação registrada."}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </AiCard>
      )}
    </Box>
  );
}
